import { z } from 'zod/v4';

/**
 * The queue processor that ran an AI processing job: live or archived to
 * history. A job that never went through the queue but has a result is
 * reported as a synthetic `skipped` processor.
 */
export const AIProcessingJobQueueJobProcessorSchema = z.object({
  id: z.string().nullable(),
  jobId: z.string().nullable(),
  processorNamespace: z.string().nullable(),
  status: z.string(),
  priority: z.number(),
  scheduledAt: z.number().nullable(),
  processedAt: z.number().nullable().optional(),
  processingStartedAt: z.number().nullable().optional(),
  attempts: z.number(),
  maxAttempts: z.number(),
  error: z.string().nullable().optional(),
  createdAt: z.date(),
});

export type AIProcessingJobQueueJobProcessor = z.infer<
  typeof AIProcessingJobQueueJobProcessorSchema
>;

/** One AI processing job run on a file, as the admin file page lists it. */
export const AIProcessingJobSchema = z.object({
  id: z.string().min(1),
  fileId: z.string().min(1),
  jobType: z.string().min(1),
  input: z.string().nullable(),
  result: z.any().nullable(),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  inputTokens: z.number().nullable(),
  outputTokens: z.number().nullable(),
  totalTokens: z.number().nullable(),
  completedInMs: z.number().nullable(),
  createdAt: z.coerce.date(),
  queueJobProcessor:
    AIProcessingJobQueueJobProcessorSchema.nullable().optional(),
});

export type AIProcessingJob = z.infer<typeof AIProcessingJobSchema>;

/** One of a company's AI processing jobs, with who created it. */
export const CompanyAIProcessingJobSchema = AIProcessingJobSchema.extend({
  createdByCompanyId: z.string().nullable(),
  createdByUserId: z.string().nullable(),
  createdByApiKeyId: z.string().nullable(),
});

export type CompanyAIProcessingJob = z.infer<
  typeof CompanyAIProcessingJobSchema
>;

/**
 * Body of `POST /api/files/ai-processing-jobs/list`: a page of the caller's
 * company's AI processing jobs, optionally of one file and one job type.
 */
export const CompanyAIProcessingJobsListBodySchema = z.object({
  /** 1-based. */
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(15).default(15),
  sort: z.enum(['created_at', 'job_type']).default('created_at'),
  order: z.enum(['asc', 'desc', 'ASC', 'DESC']).default('desc'),
  fileId: z.string().min(1).optional(),
  /** Exact job type, e.g. `EXTRACTION`. */
  jobType: z.string().optional(),
});

export type CompanyAIProcessingJobsListBody = z.infer<
  typeof CompanyAIProcessingJobsListBodySchema
>;
